/**
 * SegmentedControl — multi-option toolband toggle.
 * Inline-flex of buttons inside a 1 px line border. Active segment fills with
 * ink. Mono uppercase typography matches the Toolband artboard.
 *
 * Generic over the option value type; the consumer owns the state.
 *
 * Source: .agents/design/project/artboard-radical.jsx:157-165
 */

import type { ReactNode } from 'react';

export interface SegmentedOption<T> {
  value: T;
  label: ReactNode;
}

interface SegmentedControlProps<T extends string | number> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible label for the radio group. */
  'aria-label'?: string;
  className?: string;
}

export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  className = '',
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label']}
      className={`inline-flex border border-line ${className}`}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={[
              'cursor-pointer px-2.5 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.06em] transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:-outline-offset-2',
              active
                ? 'bg-ink text-paper'
                : 'bg-transparent text-ink-3 hover:text-ink',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
